import { useState } from 'react';
import { API } from '../config/config';
import { useAuth } from './useAuth';
import {
  GetReferralPayoutsParams,
  PaginatedResponse,
  ReferralPayoutResponse,
  ReferralPayoutStatusUpdateRequest
} from '../content/applications/Referral/referral.interface';

// ReferralPayoutController
// Base: `/api/v1/referral-payouts`
export const useReferralPayout = () => {
  const { enhancedFetch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => setError(null);

  // GET `/` — List payouts (paged)
  // Query: Pageable (page, size, sort)
  // Responses: 200 PaginatedResponse<ReferralPayoutResponse>
  const getReferralPayouts = async (
    params?: GetReferralPayoutsParams
  ): Promise<PaginatedResponse<ReferralPayoutResponse> | null> => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (params?.page !== undefined) qs.append('page', String(params.page));
      if (params?.size !== undefined) qs.append('size', String(params.size));
      if (params?.sort) qs.append('sort', params.sort);

      const url = `${API.HOST}/api/v1/referral-payouts${qs.toString() ? `?${qs.toString()}` : ''}`;

      const res = await enhancedFetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      return res as PaginatedResponse<ReferralPayoutResponse>;
    } catch (err: any) {
      const msg = err?.message || 'Failed to list referral payouts';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // GET `/{id}` — Get payout
  // Responses: 200 ReferralPayoutResponse
  const getReferralPayoutById = async (
    id: string
  ): Promise<ReferralPayoutResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await enhancedFetch(
        `${API.HOST}/api/v1/referral-payouts/${id}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      return res as ReferralPayoutResponse;
    } catch (err: any) {
      const msg = err?.message || 'Failed to fetch referral payout';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // PUT `/{id}/status` — Update payout status
  // Body: ReferralPayoutStatusUpdateRequest
  // Responses: 200 ReferralPayoutResponse
  const updateReferralPayoutStatus = async (
    id: string,
    body: ReferralPayoutStatusUpdateRequest
  ): Promise<ReferralPayoutResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await enhancedFetch(
        `${API.HOST}/api/v1/referral-payouts/${id}/status`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      );
      return res as ReferralPayoutResponse;
    } catch (err: any) {
      const msg = err?.message || 'Failed to update referral payout status';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    // Actions
    getReferralPayouts,
    getReferralPayoutById,
    updateReferralPayoutStatus,

    // State
    loading,
    error,
    clearError
  };
};
